import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const items = [
    { year: "2021 — 2025", title: "Bachelor of Information Technology with Honours", subtitle: "University Tun Hussein Onn Malaysia", type: "Education" },
    { year: "2025", title: "Build it with Firebase!", subtitle: "GDG on Campus Universiti Putra Malaysia - Selangor, Malaysia", type: "Speaker" },
    { year: "2023", title: "MASUM Sports Carnival", subtitle: "Participate @ UTeM Melaka", type: "Achievement" },
    { year: "2022", title: "SUKIPT", subtitle: "Participate @ UPSI Perak", type: "Achievement" },
    { year: "2022", title: "Gold Medal, Sukan antar fakulti", subtitle: "FSKTM, UTHM", type: "Award" },
];

const Education: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"], // Fade starts ONLY when section hits top of viewport
    });

    // Smooth transition from Dark (Zinc 950) to Light (Zinc 100)
    // [0, 0.3] Fade happens in the first 30% of scroll AFTER locking to top
    // This ensures no hard line is visible as it enters
    const backgroundColor = useTransform(scrollYProgress, [0, 0.3], ["#09090b", "#f4f4f5"]);
    const textColor = useTransform(scrollYProgress, [0, 0.3], ["#ffffff", "#18181b"]);
    const borderColor = useTransform(scrollYProgress, [0, 0.3], ["rgba(255,255,255,0.1)", "rgba(24,24,27,1)"]);
    const subTextColor = useTransform(scrollYProgress, [0, 0.3], ["#a1a1aa", "#52525b"]);

    // Parallax: items move up faster than scroll
    const listY = useTransform(scrollYProgress, [0, 1], [0, -200]);

    return (
        <motion.section
            id="education"
            ref={containerRef}
            style={{ backgroundColor, color: textColor }}
            className="pt-[40vh] md:pt-[80vh] pb-16 md:pb-32 px-4 md:px-12 lg:px-24 relative min-h-[200vh] md:min-h-[300vh] flex flex-col justify-start"
        >
            {/* Massive Header */}
            <motion.div
                style={{ borderColor: borderColor }}
                className="mb-16 md:mb-24 border-b pb-8 md:pb-12"
            >
                <h2 className="text-[12vw] md:text-[8vw] leading-[0.8] font-serif font-black tracking-tighter">
                    EDUCATION &<br />ACTIVITIES
                </h2>
            </motion.div>

            {/* List with Parallax */}
            <motion.div style={{ y: listY }} className="flex flex-col">
                {items.map((item, i) => (
                    <ItemRow key={i} item={item} borderColor={borderColor} subTextColor={subTextColor} />
                ))}
            </motion.div>
        </motion.section>
    );
};

const ItemRow: React.FC<{ item: any, borderColor: any, subTextColor: any }> = ({ item, borderColor, subTextColor }) => {
    return (
        <motion.div
            style={{ borderColor: borderColor }}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="group border-b py-8 md:py-12 flex flex-col md:flex-row justify-between items-baseline hover:bg-black/5 transition-colors cursor-default px-2 md:px-4 -mx-2 md:-mx-4"
        >
            <div className="md:w-1/4 mb-3 md:mb-0">
                <motion.span style={{ color: subTextColor }} className="font-mono text-xs uppercase tracking-widest transition-colors">
                    {item.type}
                </motion.span>
            </div>

            <div className="md:w-1/2">
                <h3 className="text-2xl md:text-4xl lg:text-5xl font-serif font-medium mb-2">{item.title}</h3>
                <motion.p style={{ color: subTextColor }} className="font-light text-sm md:text-base">{item.subtitle}</motion.p>
            </div>

            <div className="md:w-1/4 text-left md:text-right mt-3 md:mt-0">
                <span className="font-mono text-base md:text-xl">{item.year}</span>
            </div>
        </motion.div>
    )
}

export default Education;
